import { Suspense, memo, useMemo } from 'react'
import { Box, CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes } from 'react-router-dom'
import {
    Applications,
    RoutePaths,
    UserPermissions,
} from '../../../constants/constants'
import { useAppSelector } from '../../../redux/hook'
import initialRoutes, { RoutesType } from '../../../routes/routes'
import NotFoundPage from '../../Miscellaneous/NotFoundPage'
import AuthenticatedLayout from '../AuthenticatedLayout'
import UnAuthenticatedLayout from '../UnAuthenticatedLayout'
import Home from './Home'
import { usePermissions } from '../../../hooks/usePermissions'

/**
 * Shown inside the layout frame while a lazily loaded screen's chunk downloads (UBQ-5897). The
 * Suspense boundary must sit INSIDE AuthenticatedLayout/UnAuthenticatedLayout: those render the
 * bordered content frame and side nav, so a boundary above them blanks the whole frame until the
 * chunk arrives (a visible flash and layout shift on every full reload).
 */
const RouteLoading: React.FC = () => (
    <Box
        id="route-loading-spinner"
        sx={{
            display: 'flex',
            flex: 1,
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '200px',
        }}
    >
        <CircularProgress />
    </Box>
)

const RoutesComponent: React.FC = memo(() => {
    const { t } = useTranslation()
    const permissions = useAppSelector((state) => state.org.permissions)
    const enabledApplications = useAppSelector(
        (state) => state.org.enabledApplications,
    )
    const { hasPermission: hasPermissioncheck } = usePermissions()
    const hasApplicationsAccess = useMemo(
        () =>
            hasPermissioncheck([
                UserPermissions.ORG_ADMIN,
                UserPermissions.SYSTEM_READER,
            ]),
        [hasPermissioncheck],
    )
    const routes = useMemo(() => {
        if (!permissions?.length) {
            return initialRoutes
        }

        return initialRoutes.map((route: RoutesType) => {
            let hasPermission: boolean

            if (route.path === RoutePaths.facilityMonitoring) {
                hasPermission =
                    enabledApplications.includes(
                        Applications.facilityMonitoring.id,
                    ) || hasApplicationsAccess
            } else if (route.path === RoutePaths.ccl) {
                hasPermission =
                    enabledApplications.includes(Applications.inTransit.id) ||
                    hasApplicationsAccess
            } else if (route.path === RoutePaths.analytics) {
                hasPermission =
                    (enabledApplications.includes(Applications.analytics.id) &&
                        hasPermissioncheck(UserPermissions.ENTITY_ANALYST)) ||
                    hasApplicationsAccess
            } else if (route.path === RoutePaths.deviceManager) {
                hasPermission =
                    (enabledApplications.includes(
                        Applications.deviceManager.id,
                    ) &&
                        (hasPermissioncheck(UserPermissions.USER_ADMIN) ||
                            hasPermissioncheck(
                                UserPermissions.DEVICE_WRITER,
                            ))) ||
                    hasApplicationsAccess
            } else {
                hasPermission = route.permissions?.some((p) =>
                    permissions.includes(p),
                )
            }

            return { ...route, hasPermission }
        })
    }, [
        permissions,
        enabledApplications,
        hasApplicationsAccess,
        hasPermissioncheck,
    ])

    return (
        <Suspense fallback={<div>{t('loadingmap')}</div>}>
            <Routes>
                {routes.map((route) => {
                    // Handle redirects
                    if (route.redirectPath) {
                        return (
                            <Route
                                key={route.path}
                                path={route.path}
                                element={
                                    <Navigate
                                        to={route.redirectPath}
                                        replace={true}
                                    />
                                }
                            />
                        )
                    }

                    const RouteComponent = route.hasPermission
                        ? route.component
                        : Home

                    // Wrap in AuthenticatedLayout for protected routes
                    const WrappedComponent = route.requiredAuthGuard ? (
                        <AuthenticatedLayout>
                            <Suspense fallback={<RouteLoading />}>
                                <RouteComponent />
                            </Suspense>
                        </AuthenticatedLayout>
                    ) : (
                        <UnAuthenticatedLayout>
                            <Suspense fallback={<RouteLoading />}>
                                <RouteComponent />
                            </Suspense>
                        </UnAuthenticatedLayout>
                    )

                    return (
                        <Route
                            key={route.path}
                            path={route.path}
                            element={WrappedComponent}
                        />
                    )
                })}
                <Route
                    path="*"
                    element={
                        <UnAuthenticatedLayout>
                            <NotFoundPage />
                        </UnAuthenticatedLayout>
                    }
                />
            </Routes>
        </Suspense>
    )
})

RoutesComponent.displayName = 'RoutesComponent'

export default RoutesComponent
